import { useState, type ReactElement } from 'react'
import { cn } from '@util'
import { isNotEmpty, type DashboardFilter, type DashboardWidget, type ReportDateRange } from '@terros/common'
import { Badge, Card, Group, Stack, Text } from '@mantine/core'
import { stringifyReportDateRange } from '@front/model'
import { NotReady } from '@components'
import { useDashboardWidgetKpi } from '../useDashboardWidgetKpi'
import { WIDGET_TYPE_LABELS } from '../dashboardWidgetConfigOptions'
import { getDashboardWidgetLayoutSize } from '../dashboardLayout'
import { getEffectiveWidgetFilter } from '../dashboardFilter'
import { getEffectiveWidgetDateRange } from '../dashboardDateRange'
import { WidgetScopeAvatars } from './WidgetScopeAvatars'
import { TableWidget } from './TableWidget'
import { NumberWidget } from './NumberWidget'
import { LineWidget } from './LineWidget'
import { LeaderboardWidget } from './LeaderboardWidget'
import { LeaderboardMetricSelector } from './LeaderboardMetricSelector'
import { GoalWidget } from './GoalWidget'

type Props = {
  dateRange: ReportDateRange | undefined
  dashboardFilter: DashboardFilter | undefined
  widget: DashboardWidget
  className?: string
  /** Overrides the card title — the hustle card shows its dashboard's name. */
  title?: string
}

export function DashboardWidgetCard({ dateRange, dashboardFilter, widget, className, title }: Props): ReactElement {
  const [selectedLeaderboardMetricIndex, setSelectedLeaderboardMetricIndex] = useState(0)
  const effectiveDateRange = getEffectiveWidgetDateRange(widget, dateRange)
  const effectiveFilter = getEffectiveWidgetFilter(widget, dashboardFilter)
  const { data, isLoading, error } = useDashboardWidgetKpi(widget, effectiveDateRange, effectiveFilter)

  const titleText = title ?? (widget.name || WIDGET_TYPE_LABELS[widget.type])
  const dateRangeLabel = stringifyReportDateRange(effectiveDateRange)
  const isCompact = getDashboardWidgetLayoutSize(widget) === 'compact'
  const leaderboardMetricIndex =
    widget.type === 'leaderboard' && isNotEmpty(widget.metrics)
      ? Math.min(selectedLeaderboardMetricIndex, widget.metrics.length - 1)
      : 0

  return (
    <Card withBorder padding={isCompact ? 'sm' : 'md'} shadow='sm' className={cn('relative z-0', className)}>
      <Stack gap={isCompact ? 'xs' : 'sm'}>
        <Group justify='space-between' wrap='nowrap'>
          <Group gap='xs' wrap='nowrap' className='min-w-0'>
            <Text className='font-semibold' lineClamp={1} size={isCompact ? 'sm' : undefined}>
              {titleText}
            </Text>
            {dateRangeLabel && (
              <Badge color='neutral' size='sm' variant='light'>
                {dateRangeLabel}
              </Badge>
            )}
          </Group>
          {widget.type === 'leaderboard' && (
            <LeaderboardMetricSelector
              metricIndex={leaderboardMetricIndex}
              widget={widget}
              onMetricIndexChange={setSelectedLeaderboardMetricIndex}
            />
          )}
        </Group>
        <div className={cn('relative', isCompact ? 'flex min-h-[112px] items-center justify-center' : 'min-h-[120px]')}>
          <WidgetContent
            dateRange={effectiveDateRange}
            error={error}
            data={data}
            isLoading={isLoading}
            leaderboardMetricIndex={leaderboardMetricIndex}
            widget={widget}
          />
        </div>
        <WidgetScopeAvatars filter={effectiveFilter} />
      </Stack>
    </Card>
  )
}

type WidgetContentProps = {
  dateRange: ReportDateRange | undefined
  leaderboardMetricIndex: number
  widget: DashboardWidget
  isLoading: boolean
  data: ReturnType<typeof useDashboardWidgetKpi>['data']
  error: ReturnType<typeof useDashboardWidgetKpi>['error']
}

function WidgetContent({
  dateRange,
  leaderboardMetricIndex,
  widget,
  isLoading,
  data,
  error,
}: WidgetContentProps): ReactElement {
  if (widget.type === 'goal') return <GoalWidget />
  if (error) return <WidgetError error={error} />
  if (isLoading) return <NotReady inline id='Widget Data' isLoading={isLoading} />
  if (!data || data.format !== 'table') return <WidgetEmpty />

  switch (widget.type) {
    case 'number':
      return <NumberWidget data={data} widget={widget} />
    case 'leaderboard':
      return <LeaderboardWidget data={data} metricIndex={leaderboardMetricIndex} widget={widget} />
    case 'line':
      return <LineWidget data={data} dateRange={dateRange} widget={widget} />
    case 'table':
      return <TableWidget data={data} widget={widget} />
  }
}

function WidgetError({ error }: { error: string }): ReactElement {
  return (
    <Group justify='center'>
      <Text c='red' size='sm'>
        {error}
      </Text>
    </Group>
  )
}

function WidgetEmpty(): ReactElement {
  return (
    <Group justify='center'>
      <Text c='dimmed' size='sm'>
        No data
      </Text>
    </Group>
  )
}
